import { z } from 'zod'
import { printableItems } from './plan-times.ts'

/**
 * A plan's songs as a Spotify playlist (kept in ForHim's own accounts,
 * docs/15-MUSIC-AND-AI.md): which track each song is, what it is called and
 * which songs it holds.
 *
 * A Spotify link on the arrangement or the song (open.spotify.com/track/…)
 * says it outright — that is how a church fixes a wrong match. Without one
 * the song is searched for by its title and artist, and a result counts only
 * if its name is the song's title (a live version or a "- Radio Edit" still
 * counts); one by the song's artist wins, then one by its authors.
 */

/** A track id from a Spotify link or URI, or null for anything else. */
export function spotifyTrackId(url: string): string | null {
  const uri = /^spotify:track:([A-Za-z0-9]{22})$/.exec(url.trim())
  if (uri) return uri[1] ?? null
  let parsed: URL
  try {
    parsed = new URL(url.trim())
  } catch {
    return null
  }
  if (parsed.hostname !== 'open.spotify.com' && parsed.hostname !== 'play.spotify.com') return null
  // /track/{id}, or /intl-es/track/{id} on a localized link; ?si= is ignored.
  const match = /^\/(?:intl-[a-z-]+\/)?track\/([A-Za-z0-9]{22})\/?$/i.exec(parsed.pathname)
  return match?.[1] ?? null
}

/** The comparable form of a title: no accents, case, brackets, punctuation or "- Live" tails. */
export function normalizeTrackTitle(title: string): string {
  return title
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+-\s+.*$/, '')
    .replace(/[([{].*?[)\]}]/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

export interface TrackCandidate {
  name: string
  artists: readonly string[]
}

export interface SongToMatch {
  title: string
  /** Who recorded it — the strongest sign a result is this song. */
  artist: string | null
  /** Who wrote it: often who sings it too, so a weaker sign. */
  author: string | null
}

/**
 * Is one of the track's artists one of `names` (a song's artist or author
 * line), as whole words either way: "Hillsong" is "Hillsong Worship", "John
 * Newton" is "John Newton Choir" — but "Ann" is not "Joanna".
 */
function namedIn(names: string | null, track: TrackCandidate) {
  const people = (names ?? '')
    .split(/,|&|\band\b|\by\b|\/|\bfeat\.?|\bft\./i)
    .map((part) => normalizeTrackTitle(part))
    .filter(Boolean)
  if (people.length === 0) return false
  const within = (outer: string, inner: string) => ` ${outer} `.includes(` ${inner} `)
  return track.artists.some((artist) => {
    const name = normalizeTrackTitle(artist)
    return name !== '' && people.some((person) => within(name, person) || within(person, name))
  })
}

/**
 * How well a track matches a song — 0 when it is not that song at all (the
 * title must be the song's title, or the title then more: a subtitle). By the
 * song's artist counts most, then by one of its authors, then the exact
 * title over a subtitled one.
 */
export function trackScore(song: SongToMatch, track: TrackCandidate): number {
  const title = normalizeTrackTitle(song.title)
  if (!title) return 0
  const name = normalizeTrackTitle(track.name)
  const exact = name === title
  if (!exact && !name.startsWith(`${title} `)) return 0
  const by = namedIn(song.artist, track) ? 4 : namedIn(song.author, track) ? 2 : 0
  return 1 + by + (exact ? 1 : 0)
}

/** A track that is by the song's artist or one of its authors — nothing better to find. */
export const BY_THEM = 3

/** The best of `candidates` for a song, or null when none is that song. Ties keep Spotify's order. */
export function pickTrack<T extends TrackCandidate>(
  song: SongToMatch,
  candidates: readonly T[],
): { track: T; score: number } | null {
  let best: { track: T; score: number } | null = null
  for (const track of candidates) {
    const score = trackScore(song, track)
    if (score > (best?.score ?? 0)) best = { track, score }
  }
  return best
}

/**
 * What to ask Spotify's search, most specific first: title and artist as
 * fields, then title with the artist (or first author) as words, then the
 * title alone. The caller stops at the first answer by them.
 */
export function trackSearches(song: SongToMatch): string[] {
  const title = song.title.replace(/"/g, '').trim()
  const who = song.artist?.trim() || firstAuthor(song.author)
  const searches = [
    ...(song.artist?.trim()
      ? [`track:"${title}" artist:"${song.artist.replace(/"/g, '').trim()}"`]
      : []),
    ...(who ? [`${title} ${who}`] : []),
    title,
  ]
  return [...new Set(searches)]
}

/** The first author of an author line ("Brian Johnson, Jenn Johnson" → Brian Johnson). */
export function firstAuthor(author: string | null): string | null {
  const first = author
    ?.split(/,|&|\band\b|\by\b|\//)
    .map((part) => part.trim())
    .find(Boolean)
  return first ?? null
}

/**
 * The playlist's name: the plan's title (or its service type), its day and
 * the church — "Sunday Morning · Oct 25 · Grace Community". No year: a
 * playlist ends 30 days after its plan (`PLAYLIST_ENDS_AFTER_DAYS`). Kept
 * under Spotify's 100 characters by shortening the church's name.
 */
export function playlistName(plan: {
  title: string | null
  serviceTypeName: string
  date: string
  churchName: string
}) {
  const day = new Date(`${plan.date}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  const head = `${(plan.title ?? plan.serviceTypeName).trim()} · ${day}`.slice(0, 80)
  const room = 100 - head.length - 3
  const church = plan.churchName.trim()
  if (!church || room < 4) return head
  return `${head} · ${church.length > room ? `${church.slice(0, room - 1).trimEnd()}…` : church}`
}

/** What a playlist says about itself: no names, no plan details beyond its songs. */
export const PLAYLIST_DESCRIPTION =
  'The songs for this service, in running order. Made with ForHim.'

/** A playlist ends (its plan long past) this many days after the plan's last time. */
export const PLAYLIST_ENDS_AFTER_DAYS = 30

/**
 * The songs a plan's playlist holds: the running order's items linked to a
 * song, During only (Before and After — the pre-service loop, the postlude —
 * are not the service; `printableItems`), each song once, where it first
 * comes.
 */
export function playlistSongs<T extends { songId: string | null; servicePosition?: string }>(
  items: readonly T[],
): (T & { songId: string })[] {
  const seen = new Set<string>()
  const out: (T & { songId: string })[] = []
  for (const item of printableItems(items)) {
    const songId = item.songId
    if (!songId || seen.has(songId)) continue
    seen.add(songId)
    out.push({ ...item, songId })
  }
  return out
}

/** Whether a playlist whose plan's last day is `lastDate` has ended by `today` (both YYYY-MM-DD). */
export function playlistEnded(lastDate: string, today: string): boolean {
  const last = Date.parse(`${lastDate}T00:00:00Z`)
  const now = Date.parse(`${today}T00:00:00Z`)
  return now - last > PLAYLIST_ENDS_AFTER_DAYS * 86_400_000
}

/**
 * A ForHim Spotify account's settings, as a platform operator changes them:
 * whether it takes new playlists, and how many it may hold. Spotify allows
 * about 10,000 playlists an account (docs/15); the default capacity, 9,000,
 * stays under it.
 */
export const SPOTIFY_ACCOUNT_MAX_CAPACITY = 11_000
export const spotifyAccountSchema = z.object({
  status: z.enum(['active', 'full']),
  capacity: z.coerce
    .number<string | number>()
    .int('A whole number')
    .min(0, 'At least 0')
    .max(
      SPOTIFY_ACCOUNT_MAX_CAPACITY,
      `At most ${SPOTIFY_ACCOUNT_MAX_CAPACITY.toLocaleString('en-US')}`,
    ),
})
export type SpotifyAccountInput = z.output<typeof spotifyAccountSchema>
export type SpotifyAccountFormInput = z.input<typeof spotifyAccountSchema>
